import { useEffect, useRef, useState } from "react";
import { LuCheck, LuChevronDown, LuX } from "react-icons/lu";
import "./MultiSelectDrawer.scss";

const VARIANT_CLASSES = {
    default: {
        helper: "plan-manager-select-field__helper",
        error: "plan-manager-select-field__error",
        applyErrorBorder: true,
    },
    notification: {
        helper: "plan-notification-field__helper",
        error: "plan-notification-field__error",
        applyErrorBorder: false,
    },
};

/**
 * Controlled multi-select drawer/combobox field for Plan Manager forms.
 * Renders badges for selected options and a checkbox drawer for selection.
 *
 * @param {object} props
 * @param {string} props.id - Trigger element id.
 * @param {string} props.label - Field label text.
 * @param {Array<string>} [props.value=[]] - Selected values array.
 * @param {(selected: Array<string>) => void} props.onChange - Emits updated selection array.
 * @param {ReadonlyArray<{ value: string, label: string, disabled?: boolean }>} props.options - Option list.
 * @param {string} [props.placeholderOption="Select"] - Placeholder text when no items are selected.
 * @param {string} [props.helperText] - Hint text below the field.
 * @param {string} [props.error] - Validation error message.
 * @param {boolean} [props.disabled] - Disables the component.
 * @param {string} [props.className] - Extra CSS classes.
 * @param {number} [props.limit=2] - Maximum number of badge tags before "+N" count tag.
 * @param {"default" | "notification"} [props.variant="default"] - Style variant.
 * @returns {import('react').JSX.Element}
 */
const MultiSelectDrawer = ({
    id,
    label,
    value = [],
    onChange,
    options = [],
    placeholderOption = "Select",
    helperText,
    error,
    disabled = false,
    className,
    limit = 2,
    variant = "default",
}) => {
    const [isOpen, setIsOpen] = useState(false);
    const controlRef = useRef(null);
    const listboxId = `${id}-drawer-listbox`;

    const safeValue = Array.isArray(value)
        ? value
        : value
          ? [String(value)]
          : [];

    const variantClasses = VARIANT_CLASSES[variant] ?? VARIANT_CLASSES.default;
    const hasSelections = safeValue.length > 0;

    const visibleValues = safeValue.slice(0, limit);
    const hiddenCount = Math.max(safeValue.length - limit, 0);

    const rootClassName = [
        "form-input",
        "plan-manager-multiselect-drawer",
        error && variantClasses.applyErrorBorder
            ? "plan-manager-multiselect-drawer--error"
            : null,
        className,
    ]
        .filter(Boolean)
        .join(" ");

    const chevronClassName = [
        "plan-manager-multiselect-drawer__chevron",
        isOpen ? "plan-manager-multiselect-drawer__chevron--open" : null,
    ]
        .filter(Boolean)
        .join(" ");

    const closeDrawer = () => {
        setIsOpen(false);
    };

    const toggleOpen = () => {
        if (disabled) {
            return;
        }
        setIsOpen((previousIsOpen) => !previousIsOpen);
    };

    const handleOptionToggle = (optionValue, isOptionDisabled) => {
        if (isOptionDisabled || disabled) {
            return;
        }

        const isCurrentlySelected = safeValue.includes(optionValue);
        const nextValue = isCurrentlySelected
            ? safeValue.filter((val) => val !== optionValue)
            : [...safeValue, optionValue];

        onChange(nextValue);
    };

    const handleClearAll = (event) => {
        event.stopPropagation();
        if (disabled) {
            return;
        }
        onChange([]);
    };

    const handleTriggerKeyDown = (event) => {
        if (disabled) {
            return;
        }

        if (event.key === "Escape") {
            event.preventDefault();
            closeDrawer();
            return;
        }

        if (
            event.key === "ArrowDown" ||
            event.key === "Enter" ||
            event.key === " "
        ) {
            event.preventDefault();
            if (event.key === "ArrowDown" || !isOpen) {
                setIsOpen(true);
            } else {
                closeDrawer();
            }
        }
    };

    const handleListKeyDown = (event) => {
        if (event.key === "Escape") {
            event.preventDefault();
            closeDrawer();
        }
    };

    useEffect(() => {
        if (!isOpen) {
            return undefined;
        }

        const handleDocumentMouseDown = (event) => {
            if (!controlRef.current?.contains(event.target)) {
                closeDrawer();
            }
        };

        document.addEventListener("mousedown", handleDocumentMouseDown);

        return () => {
            document.removeEventListener("mousedown", handleDocumentMouseDown);
        };
    }, [isOpen]);

    useEffect(() => {
        if (disabled) {
            closeDrawer();
        }
    }, [disabled]);

    return (
        <div className={rootClassName}>
            <label htmlFor={id}>{label}</label>
            <div
                className="plan-manager-multiselect-drawer__control"
                ref={controlRef}
            >
                <button
                    type="button"
                    id={id}
                    className="plan-manager-multiselect-drawer__trigger"
                    aria-haspopup="listbox"
                    aria-expanded={isOpen}
                    aria-controls={listboxId}
                    disabled={disabled}
                    onClick={toggleOpen}
                    onKeyDown={handleTriggerKeyDown}
                >
                    <div className="plan-manager-multiselect-drawer__trigger-content">
                        {hasSelections ? (
                            <div className="plan-manager-multiselect-drawer__badges">
                                {visibleValues.map((val) => {
                                    const matched = options.find(
                                        (opt) => opt.value === val,
                                    );
                                    const displayLabel = matched?.label ?? val;
                                    return (
                                        <span
                                            key={val}
                                            className="plan-manager-multiselect-drawer__badge"
                                        >
                                            {displayLabel}
                                        </span>
                                    );
                                })}
                                {hiddenCount > 0 ? (
                                    <span className="plan-manager-multiselect-drawer__badge plan-manager-multiselect-drawer__badge--count">
                                        +{hiddenCount}
                                    </span>
                                ) : null}
                            </div>
                        ) : (
                            <span className="plan-manager-multiselect-drawer__placeholder">
                                {placeholderOption}
                            </span>
                        )}
                    </div>
                    <div className="plan-manager-multiselect-drawer__actions">
                        {hasSelections && !disabled ? (
                            <span
                                role="button"
                                tabIndex={0}
                                aria-label="Clear all selections"
                                className="plan-manager-multiselect-drawer__clear-btn"
                                onClick={handleClearAll}
                                onKeyDown={(event) => {
                                    if (
                                        event.key === "Enter" ||
                                        event.key === " "
                                    ) {
                                        handleClearAll(event);
                                    }
                                }}
                            >
                                <LuX size={14} />
                            </span>
                        ) : null}
                        <span className={chevronClassName} aria-hidden>
                            <LuChevronDown size={16} />
                        </span>
                    </div>
                </button>

                {isOpen && !disabled ? (
                    <ul
                        id={listboxId}
                        role="listbox"
                        aria-multiselectable="true"
                        className="plan-manager-multiselect-drawer__drawer"
                        onKeyDown={handleListKeyDown}
                    >
                        {options.map((option) => {
                            const isSelected = safeValue.includes(option.value);
                            return (
                                <li
                                    key={option.value}
                                    role="option"
                                    aria-selected={isSelected}
                                    aria-disabled={Boolean(option.disabled)}
                                    className={[
                                        "plan-manager-multiselect-drawer__option",
                                        isSelected
                                            ? "plan-manager-multiselect-drawer__option--selected"
                                            : null,
                                        option.disabled
                                            ? "plan-manager-multiselect-drawer__option--disabled"
                                            : null,
                                    ]
                                        .filter(Boolean)
                                        .join(" ")}
                                    onClick={() =>
                                        handleOptionToggle(
                                            option.value,
                                            option.disabled,
                                        )
                                    }
                                >
                                    <span
                                        className={[
                                            "plan-manager-multiselect-drawer__checkbox",
                                            isSelected
                                                ? "plan-manager-multiselect-drawer__checkbox--checked"
                                                : null,
                                        ]
                                            .filter(Boolean)
                                            .join(" ")}
                                        aria-hidden
                                    >
                                        {isSelected ? (
                                            <LuCheck size={12} />
                                        ) : null}
                                    </span>
                                    <span className="plan-manager-multiselect-drawer__option-label">
                                        {option.label}
                                    </span>
                                </li>
                            );
                        })}
                    </ul>
                ) : null}
            </div>
            {helperText ? (
                <p className={variantClasses.helper}>{helperText}</p>
            ) : null}
            {error ? (
                <p className={variantClasses.error} role="alert">
                    {error}
                </p>
            ) : null}
        </div>
    );
};

export default MultiSelectDrawer;
